import { useEffect, useRef, useState } from 'react'

/**
 * Canonical search input primitive.
 *
 * Wraps the legacy `.search-box` class from crm/index.html and adds:
 *   - debounced onChange (default 350ms — matches the canonical
 *     `debounce('c', …, 350)` rhythm from the legacy SPA)
 *   - inline × clear button when the field is non-empty
 *
 * The × button is an icon-only intra-input affordance, not a general
 * button. It's intentionally NOT routed through <Button> — adding a
 * variant for one consumer violates the two-consumer rule. Its styles
 * are primitive-internal (allowed per shared/ui/README.md).
 *
 * Behavior notes:
 *   - Internal `draft` state means typing is local and only fires
 *     `onChange` after the debounce window. Clearing via the × button
 *     fires `onChange('')` immediately (intentional action, no debounce).
 *   - `value` is the initial value; subsequent external `value` changes
 *     do NOT re-sync the draft. This matches the existing
 *     Accounts/Contacts toolbar behavior (parent owns the search state
 *     downstream of this input; no upstream resync needed).
 */

interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  debounceMs?: number
}

const DEFAULT_DEBOUNCE_MS = 350

export function SearchInput({
  value,
  onChange,
  placeholder,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: SearchInputProps) {
  const [draft, setDraft] = useState(value)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (timer.current !== null) clearTimeout(timer.current)
    timer.current = setTimeout(() => onChange(draft), debounceMs)
    return () => {
      if (timer.current !== null) clearTimeout(timer.current)
    }
  }, [draft, debounceMs, onChange])

  const clear = () => {
    // Cancel any pending debounce so we don't fire a stale value after the clear.
    if (timer.current !== null) clearTimeout(timer.current)
    setDraft('')
    onChange('')
  }

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <input
        className="search-box"
        type="text"
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        style={{ paddingRight: 28 }}
      />
      {draft && (
        <button
          type="button"
          onClick={clear}
          title="Clear search"
          aria-label="Clear search"
          style={{
            position: 'absolute',
            right: 6,
            background: 'none',
            border: 'none',
            color: 'var(--text-dim)',
            fontSize: 16,
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          ×
        </button>
      )}
    </div>
  )
}
